import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Receipt, TrendingUp, FileText,
  CheckSquare, Wallet,
  CreditCard, RepeatIcon, Target, Bot, CalendarClock, CalendarDays, ArrowLeftRight, Gauge
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import ProfileModal from './ProfileModal';
import AppSidebar from './AppSidebar';
import { resolveDashboardCards } from '../utils/dashboardCards';

const navItemsRaw = [
  { path: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: 'lancamentos', label: 'Lançamentos', icon: Receipt },
  { path: 'fluxo-caixa', label: 'Fluxo de Caixa', icon: TrendingUp },
  { path: 'calendario', label: 'Calendário', icon: CalendarDays },
  { path: 'desempenho', label: 'Meu Desempenho', icon: Gauge },
  { path: 'relatorios', label: 'Relatórios', icon: FileText },
  { path: 'importar-exportar', label: 'Importar e Exportar', icon: ArrowLeftRight },
  { path: 'tarefas', label: 'Tarefas & Notas', icon: CheckSquare },
  { path: 'carteira', label: 'Carteira', icon: Wallet },
  { path: 'cartoes', label: 'Cartão de Crédito', icon: CreditCard },
  { path: 'contas-pagar', label: 'Contas a Pagar', icon: CalendarClock },
  { path: 'recorrencias', label: 'Recorrências', icon: RepeatIcon },
  { path: 'metas', label: 'Metas', icon: Target },
  { path: 'chat', label: 'Assistente IA', icon: Bot },
  // { path: 'whatsapp', label: 'WhatsApp Bot', icon: MessageCircle }, // desabilitado temporariamente
];

// Itens opcionais: só aparecem se o usuário ativar em Configurações > Dashboard > Menu lateral
const OPTIONAL_NAV = { 'fluxo-caixa': 'menu_fluxo_caixa' };

// Itens que ficam dentro do grupo "Mais opções" (o resto fica sempre à vista)
const MORE_PATHS = ['fluxo-caixa', 'calendario', 'desempenho', 'contas-pagar', 'recorrencias', 'metas', 'relatorios', 'importar-exportar', 'tarefas'];

const GastifyMark = () => (
  <img
    src="/gastify-icon-192.png"
    alt="Gastify"
    className="w-10 h-10 rounded-2xl object-cover flex-shrink-0 shadow-md border border-teal-500/30"
    data-testid="gastify-brand-mark"
  />
);

export default function Sidebar() {
  const { user, logout, refreshUser } = useAuth();
  const { tenant } = useTenant();
  const { tenantSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate(`/${tenantSlug}/login`); };

  const prefs = resolveDashboardCards(user);
  const toItem = ({ path, label, icon }) => ({
    key: path,
    label,
    icon,
    to: `/${tenantSlug}/${path}`,
    active: location.pathname.endsWith(`/${path}`),
    testId: `sidebar-${label.toLowerCase().replace(/\s+/g, '-').replace('&', 'e')}-link`,
  });
  const visible = navItemsRaw.filter(({ path }) => !OPTIONAL_NAV[path] || prefs[OPTIONAL_NAV[path]]);
  const items = visible.filter(({ path }) => !MORE_PATHS.includes(path)).map(toItem);
  const moreItems = visible.filter(({ path }) => MORE_PATHS.includes(path)).map(toItem);

  return (
    <>
      <AppSidebar
        logo={tenant.logo}
        fallbackLogo={<GastifyMark />}
        title={tenant.nomeDoSistema || 'GASTIFY'}
        subtitle={tenant.nomeEmpresa || 'Gestão Financeira'}
        mobileTitle={tenant.nomeDoSistema || 'Gastify'}
        items={items}
        moreItems={moreItems}
        user={{
          name: user?.nomeCompleto || user?.username,
          username: user?.username,
          fotoPerfil: user?.fotoPerfil,
        }}
        showMobileTopBar={false}
        onOpenProfile={() => setProfileOpen(true)}
        onLogout={handleLogout}
      />
      <ProfileModal isOpen={profileOpen} onClose={() => setProfileOpen(false)}
        kind="tenant" currentUser={user} onSuccess={refreshUser} />
    </>
  );
}
